import { PREFIX } from './devStorage';
import { mediaSrc } from './mediaUrl';
import { getTenantId } from './tenant';

const PHOTO_INDEX_KEY = 'finance_photo_index_v1';
const FINANCE_RECORDS_SUFFIX = '_finance_records_v1';

type PhotoIndex = {
  byHa: Record<string, string>;
  byMobile: Record<string, string>;
};

function orgStorageKey(rawKey: string): string {
  const tid = getTenantId() ?? 'anon';
  return `${PREFIX}${tid}_org_${rawKey}`;
}

function readIndex(): PhotoIndex {
  try {
    const raw = localStorage.getItem(orgStorageKey(PHOTO_INDEX_KEY));
    if (!raw) return { byHa: {}, byMobile: {} };
    const parsed = JSON.parse(raw) as Partial<PhotoIndex>;
    return {
      byHa: parsed.byHa ?? {},
      byMobile: parsed.byMobile ?? {},
    };
  } catch {
    return { byHa: {}, byMobile: {} };
  }
}

function writeIndex(index: PhotoIndex) {
  try {
    localStorage.setItem(orgStorageKey(PHOTO_INDEX_KEY), JSON.stringify(index));
  } catch {
    /* quota */
  }
}

/** Resolve applicant photo from finance record data (supports legacy field names). */
export function resolveRecordPhoto(data: Record<string, unknown> | null | undefined): string {
  if (!data) return '';
  const keys = ['applicantPhoto', 'avatar', 'photo', 'customerPhoto', 'photoUrl'];
  for (const k of keys) {
    const v = mediaSrc(String(data[k] ?? ''));
    if (v) return v;
  }
  return '';
}

/** Remember applicant photos org-wide so HA lookup works across ledgers on this device. */
export function cacheFinanceApplicantPhoto(data: Record<string, unknown>) {
  const photo = resolveRecordPhoto(data);
  if (!photo) return;

  const index = readIndex();
  const ha = String(data.haNumber ?? '')
    .trim()
    .toLowerCase();
  const mobile = String(data.mobileNumber ?? '').replace(/\D/g, '');
  if (ha) index.byHa[ha] = photo;
  if (mobile) index.byMobile[mobile] = photo;
  writeIndex(index);
}

export function readCachedApplicantPhoto(haNumber: string, mobileNumber?: string): string {
  const index = readIndex();
  const ha = haNumber.trim().toLowerCase();
  if (ha && index.byHa[ha]) return index.byHa[ha];

  const mobile = String(mobileNumber ?? '').replace(/\D/g, '');
  if (mobile && index.byMobile[mobile]) return index.byMobile[mobile];

  return '';
}

/** Scan every ledger cache for this tenant (same browser) and find a stored photo. */
export function scanTenantFinancePhotosForHa(haNumber: string): string {
  const record = findTenantFinanceRecordByHa(haNumber);
  return resolveRecordPhoto(record ?? undefined);
}

/** Find a finance record by HA across all ledger caches on this device. */
export function findTenantFinanceRecordByHa(haNumber: string): Record<string, unknown> | null {
  const tid = getTenantId();
  const ha = haNumber.trim();
  if (!tid || !ha) return null;

  const normalize = (v: string) =>
    String(v ?? '')
      .trim()
      .toLowerCase()
      .replace(/^ha[\s\-_]*/i, '')
      .replace(/[\s\-_]/g, '');
  const needle = normalize(ha);
  if (!needle) return null;

  const prefix = `${PREFIX}${tid}_`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (!storageKey?.startsWith(prefix) || !storageKey.endsWith(FINANCE_RECORDS_SUFFIX)) continue;
      const raw = localStorage.getItem(storageKey);
      if (!raw) continue;
      const records = JSON.parse(raw) as Array<{ data?: Record<string, unknown> }>;
      if (!Array.isArray(records)) continue;
      for (const record of records) {
        const recordHa = String(record.data?.haNumber ?? '');
        const match =
          normalize(recordHa) === needle || recordHa.trim().toLowerCase() === ha.toLowerCase();
        if (match && record.data) {
          cacheFinanceApplicantPhoto(record.data);
          return record.data;
        }
      }
    }
  } catch {
    /* ignore parse errors */
  }
  return null;
}

/** Apply photo fallbacks onto a finance prefill patch. */
export function withApplicantPhoto(
  patch: Record<string, unknown>,
  haNumber: string,
): Record<string, unknown> {
  const next = { ...patch };
  let photo = resolveRecordPhoto(next);
  if (!photo) photo = readCachedApplicantPhoto(haNumber, String(next.mobileNumber ?? ''));
  if (!photo) photo = scanTenantFinancePhotosForHa(haNumber);
  if (photo) next.applicantPhoto = photo;
  return next;
}
